import { useState, useMemo, useEffect } from 'react';
import { Modal, Form, Input, message, Select, Upload } from 'antd';
import { ExclamationCircleOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Card, Text } from '@bancobarigui/barihaus';

import { inspectionIssuesService } from '@/services/inspectionIssues.service';
import {
  InspectionIssueTypeLabels,
  InspectionIssueAttachment,
  InspectorIssueTypes,
  ProviderIssueTypes,
  BankIssueTypes,
} from '@/services/types/inspectionIssues.types';
import {
  inspectionIssueTypeParamService,
  type InspectionIssueTypeParam,
} from '@/services/inspectionIssueTypeParam.service';
import { usePermissions } from '@/shared/hooks/usePermissions';

interface CreateIssueModalProps {
  open: boolean;
  onClose: () => void;
  inspectionItemId: string;
  onSuccess?: () => void;
}

interface FormValues {
  type: number | string;
  title: string;
  description: string;
}

export function CreateIssueModal({
  open,
  onClose,
  inspectionItemId,
  onSuccess
}: CreateIssueModalProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<InspectionIssueAttachment[]>([]);
  const [uploading, setUploading] = useState(false);

  const { isInspectorUser, isProviderUser, isBankUser } = usePermissions();
  const [dynamicTypes, setDynamicTypes] = useState<InspectionIssueTypeParam[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Determina o sourceRole do usuário atual
  const currentSourceRole = useMemo(() => {
    if (isInspectorUser()) return 'Inspector';
    if (isProviderUser()) return 'Provider';
    if (isBankUser()) return 'Bank';
    return '';
  }, [isInspectorUser, isProviderUser, isBankUser]);

  // Busca tipos dinâmicos do backend quando o modal abre
  useEffect(() => {
    if (!open || !currentSourceRole) return;
    setLoadingTypes(true);
    inspectionIssueTypeParamService
      .getByRole(currentSourceRole)
      .then((types) => {
        if (types && types.length > 0) {
          setDynamicTypes(types);
        }
      })
      .catch(() => {
        // fallback: usa tipos hardcoded se API falhar
        setDynamicTypes([]);
      })
      .finally(() => setLoadingTypes(false));
  }, [open, currentSourceRole]);

  // Fallback para tipos hardcoded caso API não retorne dados
  const { targetRole: fallbackTargetRole, availableTypes: fallbackTypes } = useMemo(() => {
    if (isInspectorUser()) {
      return { targetRole: 'Provider', availableTypes: InspectorIssueTypes };
    }
    if (isProviderUser()) {
      return { targetRole: 'Bank', availableTypes: ProviderIssueTypes };
    }
    if (isBankUser()) {
      return { targetRole: 'Provider', availableTypes: BankIssueTypes };
    }
    return { targetRole: 'Provider', availableTypes: [] };
  }, [isInspectorUser, isProviderUser, isBankUser]);

  const useDynamic = dynamicTypes.length > 0;

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      let resolvedTargetRole = fallbackTargetRole;
      let issueTypeId: string | undefined;
      let typeNum = 99; // default to Outros

      if (useDynamic) {
        const selectedType = dynamicTypes.find((t) => t.id === values.type);
        if (selectedType) {
          resolvedTargetRole = selectedType.targetRole === 'All' ? fallbackTargetRole : selectedType.targetRole;
          issueTypeId = selectedType.id;
        }
      } else {
        typeNum = values.type as number;
      }

      await inspectionIssuesService.createIssue(inspectionItemId, {
        inspectionItemId,
        type: typeNum,
        issueTypeId,
        title: values.title,
        description: values.description,
        targetRole: resolvedTargetRole,
        attachments: attachments.length > 0 ? attachments : []
      });

      message.success('Pendência cadastrada com sucesso!');
      form.resetFields();
      setAttachments([]);
      onSuccess?.();
      onClose();
    } catch {
      message.error('Erro ao cadastrar pendência');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAttachments([]);
    onClose();
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newAttachment: InspectionIssueAttachment = {
        fileName: file.name,
        fileUrl: `https://storage.bari.com/issues/${file.name}`,
        bucketPath: `issues/${new Date().toISOString().split('T')[0]}/${file.name}`,
        bucketName: 'bari-inspection-attachments',
        fileType: file.type,
        fileSizeBytes: file.size
      };

      setAttachments(prev => [...prev, newAttachment]);
      message.success('Arquivo anexado com sucesso!');
    } catch (error) {
      message.error('Erro ao anexar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const typeOptions = useDynamic
    ? dynamicTypes.map((t) => ({ value: t.id, label: t.name }))
    : fallbackTypes.map((typeValue) => ({
        value: typeValue,
        label: InspectionIssueTypeLabels[typeValue] || `Tipo ${typeValue}`,
      }));

  const targetLabel = fallbackTargetRole === 'Bank' ? 'Banco' : 'Prestadora';

  if (!open) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <ExclamationCircleOutlined className="text-orange-500" />
          <Text strong>Cadastrar Pendência</Text>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <div className="py-4">
        <Card className="mb-6 bg-orange-50 border-orange-200">
          <div className="flex items-start gap-3">
            <ExclamationCircleOutlined className="text-orange-500 mt-1" />
            <div>
              <Text strong className="text-orange-900 block mb-1">
                Cadastro de Pendência → {targetLabel}
              </Text>
              <Text className="text-orange-700 text-sm">
                Descreva detalhadamente o problema encontrado.
                Esta pendência será direcionada para a <strong>{targetLabel}</strong>.
              </Text>
            </div>
          </div>
        </Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="type"
            label="Tipo de Pendência"
            rules={[
              { required: true, message: 'Por favor, selecione o tipo de pendência' }
            ]}
          >
            <Select
              placeholder="Selecione o tipo de pendência"
              size="large"
              options={typeOptions as { value: string; label: string }[]}
            />
          </Form.Item>

          <Form.Item
            name="title"
            label="Título da Pendência"
            rules={[
              { required: true, message: 'Por favor, informe um título' },
              { min: 5, message: 'O título deve ter pelo menos 5 caracteres' }
            ]}
          >
            <Input
              placeholder="Ex: Dificuldade para agendar inspeção"
              size="large"
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descrição Detalhada"
            rules={[
              { required: true, message: 'Por favor, descreva a pendência' },
              { min: 20, message: 'A descrição deve ter pelo menos 20 caracteres' }
            ]}
          >
            <Input.TextArea
              placeholder="Descreva detalhadamente o problema encontrado, as tentativas realizadas e qualquer informação relevante..."
              rows={5}
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item label="Anexos (opcional)">
            <div className="space-y-4">
              <Upload
                beforeUpload={(file) => {
                  handleFileUpload(file);
                  return false;
                }}
                showUploadList={false}
                accept="image/*,.pdf,.doc,.docx"
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={uploading}
                  disabled={uploading}
                >
                  Anexar Arquivo
                </Button>
              </Upload>

              {attachments.length > 0 && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <Text strong className="block mb-3">Arquivos Anexados:</Text>
                  <div className="space-y-2">
                    {attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                        <div className="flex-1">
                          <Text className="font-medium">{attachment.fileName}</Text>
                          <Text className="text-sm text-gray-500 block">
                            {attachment.fileType} - {(attachment.fileSizeBytes / 1024).toFixed(1).replace('.', ',')} KB
                          </Text>
                        </div>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveAttachment(index)}
                          size="small"
                        >{' '}</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Form.Item>

          <Form.Item className="mb-0 pt-4">
            <div className="flex gap-3 justify-end">
              <Button
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                buttonColor="primary"
                htmlType="submit"
                loading={submitting}
              >
                Cadastrar Pendência
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}
